import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { challengesApi, settingsApi } from '../services/api';
import { getPlayer } from '../utils/playerStorage';

function effectivePoints(basePoints, priorCount) {
  if (priorCount === 0) return basePoints;
  if (priorCount === 1) return Math.floor(basePoints * 0.7);
  return Math.floor(basePoints * 0.5);
}

export default function GenericChallengesPage() {
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completingId, setCompletingId] = useState(null);
  const [hidePoints, setHidePoints] = useState(false);
  const [photos, setPhotos] = useState({});
  const fileInputRefs = useRef({});
  const navigate = useNavigate();

  const player = getPlayer() || {};

  const fetchChallenges = () => {
    challengesApi.getAll()
      .then(res => setChallenges(res.data))
      .catch(err => console.error('Failed to load challenges:', err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchChallenges();
    settingsApi.get()
      .then(res => { if (res.data.hide_points != null) setHidePoints(res.data.hide_points); })
      .catch(() => {});
  }, []);

  const handlePhotoChange = (challengeId, e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setPhotos(prev => ({ ...prev, [challengeId]: { file, preview: ev.target.result } }));
    };
    reader.readAsDataURL(file);
  };

  const handleComplete = async (challenge) => {
    setCompletingId(challenge.id);
    try {
      let proofPhotoUrl = '';
      const photoState = photos[challenge.id];
      if (photoState?.file) {
        const uploadRes = await challengesApi.uploadProof(challenge.id, photoState.file, player.teamId);
        proofPhotoUrl = uploadRes.data.photo_url || '';
      }
      await challengesApi.complete(challenge.id, player.teamId, proofPhotoUrl);
      fetchChallenges();
    } catch (err) {
      alert(err.response?.data?.error || 'Noe gikk galt.');
    } finally {
      setCompletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="page loading-page">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="page generic-page">
      <button className="back-btn" onClick={() => navigate('/map')}>
        ← Tilbake til kart
      </button>

      <div className="generic-header">
        <h1>Utfordringer</h1>
        <p className="subtitle">Disse kan gjøres hvor som helst</p>
      </div>

      <div className="challenges-list">
        {challenges.length === 0 ? (
          <p className="empty-state">Ingen utfordringer ennå.</p>
        ) : (
          challenges.map(challenge => {
            const isCompleted = challenge.completed_by_teams?.includes(player.teamId);
            const priorCount = isCompleted
              ? (challenge.completed_by_teams?.filter(id => id !== player.teamId).length ?? 0)
              : (challenge.completed_by_teams?.length ?? 0);
            const earned = effectivePoints(challenge.points, priorCount);
            return (
              <div
                key={challenge.id}
                className={`challenge-card ${isCompleted ? 'done' : ''}`}
              >
                <div className="card-header">
                  <h3>{challenge.title}</h3>
                  {!hidePoints && (
                    <span className="card-points">
                      {isCompleted
                        ? `${earned}p`
                        : earned < challenge.points
                          ? <>{earned}p <span style={{ opacity: 0.5, fontWeight: 400, fontSize: '0.85em', textDecoration: 'line-through' }}>{challenge.points}p</span></>
                          : `${earned}p`}
                    </span>
                  )}
                </div>
                {challenge.description && (
                  <p className="card-desc">{challenge.description}</p>
                )}
                {isCompleted ? (
                  <div className="card-done-badge">Fullført ✓</div>
                ) : (
                  <>
                    <div className="proof-photo-box proof-photo-box--card">
                      <p className="proof-photo-label">📸 Ta et bevis-bilde</p>
                      <input
                        ref={el => { fileInputRefs.current[challenge.id] = el; }}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        style={{ display: 'none' }}
                        onChange={e => handlePhotoChange(challenge.id, e)}
                      />
                      {photos[challenge.id]?.preview ? (
                        <div className="proof-preview-wrap">
                          <img src={photos[challenge.id].preview} alt="Bevis" className="proof-preview-img" />
                          <button
                            className="proof-retake-btn"
                            onClick={() => {
                              setPhotos(prev => { const n = { ...prev }; delete n[challenge.id]; return n; });
                              if (fileInputRefs.current[challenge.id]) fileInputRefs.current[challenge.id].value = '';
                            }}
                          >
                            Ta nytt bilde
                          </button>
                        </div>
                      ) : (
                        <button
                          className="proof-camera-btn"
                          onClick={() => fileInputRefs.current[challenge.id]?.click()}
                        >
                          📷 Åpne kamera
                        </button>
                      )}
                    </div>
                    <button
                      className="card-complete-btn"
                      onClick={() => handleComplete(challenge)}
                      disabled={completingId === challenge.id}
                    >
                      {completingId === challenge.id
                        ? (photos[challenge.id]?.file ? 'Laster opp...' : 'Fullfører...')
                        : 'Fullført!'}
                    </button>
                  </>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
